/**
 * Contains the types used by the backend API to represent the activity log of the admin section when
 * returning it to the frontend. Instants travel as ISO strings, which is what they arrive as, and the
 * actions travel as the metric names rather than as the enum so that the frontend needs nothing of
 * the database.
 */

/** One changed setting, or one edited field of a user. The wording of `field` is the reader's own. */
export type ActivityLogFieldChange = { field: string; value: unknown }

/**
 * What the Details column of one entry says, beyond the action itself. Each variant belongs to a set
 * of actions and carries only what those actions can explain; `kind` is what the column renders on.
 */
export type ActivityLogDetails =
  | { kind: 'SETTINGS'; changes: ActivityLogFieldChange[] }
  | { kind: 'USER'; name: string | null; changes: ActivityLogFieldChange[] }
  | { kind: 'RESOURCE'; resource: string; name: string | null }
  | { kind: 'FILE_FORMAT'; format: string }
  /** Somebody's standing on a meeting: shared with them, changed, or taken away. No level on a removal. */
  | { kind: 'ACCESS'; name: string | null; accessLevel: string | null }
  | { kind: 'RETENTION'; from: string | null; to: string | null }
  | { kind: 'REPROCESSING'; scope: 'FULL' | 'PARTIAL'; outputs: string[]; items: string[] }

export type ActivityLogEntry = {
  id: number
  metric: string
  timestamp: string
  user: { id: number; name: string } | null
  /** A Brief AI member of staff acting for the team. The real actor is never exposed. */
  performedByBriefAiSupport: boolean
  /** Nobody pressed anything: the retention sweep and the like. */
  performedAutomatically: boolean
  team: { id: number; name: string } | null
  meeting: { id: number; name: string } | null
  details: ActivityLogDetails | null
}

/**
 * When each action started being recorded, so that an empty result can be told apart from an action
 * that never happened. Entries themselves are never deleted, so a date here is the whole history.
 *
 * These are days rather than instants — `2025-04-02` — because what they name is the release that
 * began keeping the action, not a moment.
 */
export type ActivityLogCoverage = {
  /** The earliest of the per-action dates: when this log begins. */
  loggingSince: string
  /**
   * When the caller's own history starts: the earlier of their client and their oldest team.
   * An action recorded only since after this date has a stretch of their time it cannot speak for.
   */
  customerSince: string | null
  /** Every action the log reports on. A reported action always has a date; there is no "not yet". */
  actions: { metric: string; recordedSince: string }[]
}

/** The two actors that are not a person, used as their own values in the Person column's filter. */
export const ACTIVITY_LOG_SUPPORT_PERSON = 'BRIEF_AI_SUPPORT'
export const ACTIVITY_LOG_AUTOMATIC_PERSON = 'AUTOMATIC'
